import { MAX_COMMAND_LEAD_TICKS, type BattleEngine } from '../../engine/engine';
import type { BattleCommand, CardId, CommandResult, PlayerId, WorldPoint } from '../../engine/types';
import { inputBatchSchema, NETWORK_PROTOCOL_VERSION, opponentSeat, sideForSeat, type InputBatch, type NetworkSeat } from '../../network/contracts';
import type { PeerChannel } from '../../network/webrtc-peer';
import { NETWORK_MAX_INPUT_DELAY_TICKS, NETWORK_MIN_INPUT_DELAY_TICKS } from '../../network/timing';
import type { BattleDriver } from './battle-driver';

export const NETWORK_INPUT_DELAY_TICKS = NETWORK_MAX_INPUT_DELAY_TICKS;
export const NETWORK_CHECKPOINT_INTERVAL = 150;
const NETWORK_TRACE_LIMIT = 3_600;

export interface LockstepTraceEvent {
  readonly atMs: number;
  readonly kind: 'batch-sent' | 'batch-received' | 'command-staged' | 'command-enqueued' | 'checkpoint-local' | 'checkpoint-remote' | 'disconnect' | 'reconnect';
  readonly tick: number;
  readonly sequence?: number;
  readonly commandTick?: number;
  readonly commandSequence?: number;
  readonly confirmedThroughTick?: number;
}

export interface LockstepCallbacks {
  readonly onDesync: (details: { tick: number; localChecksum: string; remoteChecksum: string }) => void;
  readonly onDisconnect: () => void;
  readonly onReconnect: () => void;
  readonly onConnectionLost: () => void;
}

export class LockstepNetworkDriver implements BattleDriver {
  readonly kind = 'network' as const;
  readonly allowsPause = false;
  readonly allowsInput = true;
  readonly localSide: PlayerId;
  private readonly remoteSide: PlayerId;
  private remoteConfirmedThrough = -1;
  private localBatchSequence = 1;
  private lastRemoteSequence = 0;
  private readonly remoteCommands = new Map<number, BattleCommand[]>();
  private readonly localCommands = new Map<number, BattleCommand[]>();
  private readonly remoteCheckpoints = new Map<number, string>();
  private readonly localCheckpoints = new Map<number, string>();
  private minimumCommandTick = 0;
  private readonly unsubscribeMessage: () => void;
  private readonly unsubscribeDisconnect: () => void;
  private readonly unsubscribeReconnect: () => void;
  private readonly unsubscribeConnectionLost: () => void;
  private readonly inputDelayTicks: number;
  private readonly trace: LockstepTraceEvent[] = [];
  private disposed = false;
  private desynchronized = false;

  constructor(
    private readonly networkMatchId: string,
    private readonly localSeat: NetworkSeat,
    private readonly peer: PeerChannel,
    private readonly callbacks: LockstepCallbacks,
  ) {
    this.localSide = sideForSeat(localSeat);
    this.remoteSide = sideForSeat(opponentSeat(localSeat));
    this.inputDelayTicks = Math.max(NETWORK_MIN_INPUT_DELAY_TICKS, Math.min(MAX_COMMAND_LEAD_TICKS, peer.linkStats.inputDelayTicks));
    this.unsubscribeMessage = peer.onMessage((raw) => this.receive(raw));
    this.unsubscribeDisconnect = peer.onDisconnect(() => { this.recordTrace({ kind: 'disconnect', tick: this.minimumCommandTick }); callbacks.onDisconnect(); });
    this.unsubscribeReconnect = peer.onReconnect(() => {
      const pending = [...this.localCommands.entries()].filter(([tick]) => tick >= this.minimumCommandTick).flatMap(([, commands]) => commands).slice(0, 4);
      this.sendBatch(pending, this.minimumCommandTick + this.inputDelayTicks - 1);
      this.recordTrace({ kind: 'reconnect', tick: this.minimumCommandTick });
      callbacks.onReconnect();
    });
    this.unsubscribeConnectionLost = peer.onConnectionLost(() => callbacks.onConnectionLost());
    this.sendBatch([], this.inputDelayTicks - 1);
  }

  canAdvance(engine: BattleEngine): boolean {
    return !this.disposed && !this.desynchronized && this.remoteConfirmedThrough >= engine.currentTick;
  }

  beforeStep(engine: BattleEngine): void {
    this.minimumCommandTick = engine.currentTick;
    const commands = [
      ...(this.localCommands.get(engine.currentTick) ?? []),
      ...(this.remoteCommands.get(engine.currentTick) ?? []),
    ].sort((left, right) => left.playerId === right.playerId
      ? left.sequence - right.sequence
      : left.playerId === 'player' ? -1 : 1);
    this.localCommands.delete(engine.currentTick);
    this.remoteCommands.delete(engine.currentTick);
    for (const command of commands) {
      const result = engine.enqueue(command);
      this.recordTrace({ kind: 'command-enqueued', tick: engine.currentTick, commandTick: command.tick, commandSequence: command.sequence });
      if (!result.ok) engine.journal.record({ tick: engine.currentTick, phase: 'command', source: 'lockstep', level: 'error', code: 'NETWORK_COMMAND_REJECTED', commandSequence: command.sequence, cardIds: [command.cardId], payload: { command, reason: result.reason, localSeat: this.localSeat } });
    }
  }

  afterStep(engine: BattleEngine): void {
    this.minimumCommandTick = engine.currentTick;
    for (const tick of this.localCommands.keys()) if (tick < engine.currentTick) this.localCommands.delete(tick);
    if (engine.currentTick % NETWORK_CHECKPOINT_INTERVAL === 0) {
      const checksum = engine.checksum();
      this.localCheckpoints.set(engine.currentTick, checksum);
      this.recordTrace({ kind: 'checkpoint-local', tick: engine.currentTick });
      this.compareCheckpoint(engine.currentTick);
      this.sendBatch([], engine.currentTick + this.inputDelayTicks - 1, { tick: engine.currentTick, checksum });
    } else {
      this.sendBatch([], engine.currentTick + this.inputDelayTicks - 1);
    }
  }

  submitCard(engine: BattleEngine, cardId: CardId, position: WorldPoint): CommandResult {
    if (this.disposed || this.desynchronized) return { ok: false, reason: 'battle-ended' };
    const command: BattleCommand = {
      type: 'play-card',
      tick: engine.currentTick + this.inputDelayTicks,
      sequence: engine.state.players[this.localSide].nextSequence,
      playerId: this.localSide,
      cardId,
      position,
    };
    if ([...this.localCommands.values()].some((commands) => commands.length > 0)) return { ok: false, reason: 'invalid-sequence' };
    const result = engine.inspectCommand(command);
    if (result.ok) {
      const commands = this.localCommands.get(command.tick) ?? [];
      commands.push(command);
      this.localCommands.set(command.tick, commands);
      this.recordTrace({ kind: 'command-staged', tick: engine.currentTick, commandTick: command.tick, commandSequence: command.sequence });
      engine.journal.record({ tick: engine.currentTick, phase: 'command', source: 'lockstep', code: 'NETWORK_COMMAND_STAGED', commandSequence: command.sequence, cardIds: [command.cardId], payload: { command, localSeat: this.localSeat } });
      this.sendBatch([command], engine.currentTick + this.inputDelayTicks - 1);
    }
    return result;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribeMessage();
    this.unsubscribeDisconnect();
    this.unsubscribeReconnect();
    this.unsubscribeConnectionLost();
    this.peer.close();
  }

  debugSnapshot(_engine: BattleEngine): unknown {
    return {
      schemaVersion: 1,
      protocolVersion: NETWORK_PROTOCOL_VERSION,
      localSeat: this.localSeat,
      inputDelayTicks: this.inputDelayTicks,
      minimumCommandTick: this.minimumCommandTick,
      remoteConfirmedThrough: this.remoteConfirmedThrough,
      localBatchSequence: this.localBatchSequence,
      lastRemoteSequence: this.lastRemoteSequence,
      pendingLocalCommands: [...this.localCommands.values()].flat(),
      pendingRemoteCommands: [...this.remoteCommands.values()].flat(),
      localCheckpoints: [...this.localCheckpoints.entries()].map(([tick, checksum]) => ({ tick, checksum })),
      remoteCheckpoints: [...this.remoteCheckpoints.entries()].map(([tick, checksum]) => ({ tick, checksum })),
      transport: this.peer.debugSnapshot?.() ?? null,
      trace: [...this.trace],
    };
  }

  private sendBatch(commands: readonly BattleCommand[], confirmedThroughTick: number, checkpoint?: { tick: number; checksum: string }): void {
    if (this.disposed) return;
    const batch: InputBatch = {
      type: 'input-batch',
      protocolVersion: NETWORK_PROTOCOL_VERSION,
      networkMatchId: this.networkMatchId,
      seat: this.localSeat,
      sequence: this.localBatchSequence++,
      commands,
      confirmedThroughTick,
      ...(checkpoint ? { checkpoint } : {}),
    };
    const firstCommand = commands[0];
    this.recordTrace({ kind: 'batch-sent', tick: this.minimumCommandTick, sequence: batch.sequence, confirmedThroughTick, ...(firstCommand ? { commandTick: firstCommand.tick, commandSequence: firstCommand.sequence } : {}) });
    this.peer.send(JSON.stringify(batch));
  }

  private receive(raw: string): void {
    let decoded: unknown;
    try { decoded = JSON.parse(raw); } catch { return; }
    const parsed = inputBatchSchema.safeParse(decoded);
    if (!parsed.success) return;
    const batch = parsed.data as unknown as InputBatch;
    if (batch.networkMatchId !== this.networkMatchId || batch.seat !== opponentSeat(this.localSeat) || batch.sequence <= this.lastRemoteSequence) return;
    if (batch.confirmedThroughTick > this.minimumCommandTick + this.inputDelayTicks * 2) return;
    this.lastRemoteSequence = batch.sequence;
    this.remoteConfirmedThrough = Math.max(this.remoteConfirmedThrough, batch.confirmedThroughTick);
    const firstCommand = batch.commands[0];
    this.recordTrace({ kind: 'batch-received', tick: this.minimumCommandTick, sequence: batch.sequence, confirmedThroughTick: batch.confirmedThroughTick, ...(firstCommand ? { commandTick: firstCommand.tick, commandSequence: firstCommand.sequence } : {}) });
    for (const command of batch.commands) {
      if (command.playerId !== this.remoteSide || command.tick < this.minimumCommandTick || command.tick > batch.confirmedThroughTick + 1) continue;
      const list = this.remoteCommands.get(command.tick) ?? [];
      if (!list.some((candidate) => candidate.sequence === command.sequence)) list.push(command);
      this.remoteCommands.set(command.tick, list);
    }
    if (batch.checkpoint) {
      this.remoteCheckpoints.set(batch.checkpoint.tick, batch.checkpoint.checksum);
      this.recordTrace({ kind: 'checkpoint-remote', tick: batch.checkpoint.tick });
      this.compareCheckpoint(batch.checkpoint.tick);
    }
  }

  private compareCheckpoint(tick: number): void {
    const localChecksum = this.localCheckpoints.get(tick);
    const remoteChecksum = this.remoteCheckpoints.get(tick);
    if (!localChecksum || !remoteChecksum || localChecksum === remoteChecksum || this.desynchronized) return;
    this.desynchronized = true;
    this.callbacks.onDesync({ tick, localChecksum, remoteChecksum });
  }

  private recordTrace(event: Omit<LockstepTraceEvent, 'atMs'>): void {
    this.trace.push({ atMs: Math.round(performance.now()), ...event });
    if (this.trace.length > NETWORK_TRACE_LIMIT) this.trace.splice(0, this.trace.length - NETWORK_TRACE_LIMIT);
  }
}
